import { useEffect, useRef, useState } from "react";
import { api, day, money } from "../../api.js";
import { decodeCp1251 } from "../../../../lib/pn/cp1251.js";
import { buildUrXml, urBlankHtml, UR_FINE_PCT } from "../../../../lib/vat/ur.js";
import formCssUrl from "../../../../lib/vat/form.css?url";
import { ScaledForm } from "../pn/PnForm.jsx";
import ReportSign, { b64ToBytes } from "../ReportSign.jsx";

// «Звітність» → «Уточнити»: уточнюючий розрахунок з ПДВ (J0217026). ① графа 5 — уточнені показники (графу 4 взято з поданої
// декларації чи останнього уточнюючого, графа 6 і підсумки рахуються самі) → ② бланк і «Я переглянув(-ла)» → ③ подання
// спільним потоком ReportSign (кабінет → бланк із ДПС → підпис КЕП → квитанція)
const A4 = 794;
const MONTHS = ["січень", "лютий", "березень", "квітень", "травень", "червень", "липень", "серпень", "вересень", "жовтень", "листопад", "грудень"];
const monthName = (p) => (p ? `${MONTHS[Number(p.slice(5, 7)) - 1]} ${p.slice(0, 4)}` : "");
const grn = (v) => (v == null ? "" : Number(v).toLocaleString("uk-UA"));

export default function VatUr({ companyId, company, draftId, version }) {
  const base = `/api/companies/${companyId}/vat-ur/drafts/${draftId}`;
  const canEdit = ["preparer", "submitter", "owner"].includes(company?.role);
  const [data, setData] = useState(null), [err, setErr] = useState(""), [busy, setBusy] = useState(null);
  const [edits, setEdits] = useState({}), [all, setAll] = useState(false);
  const timer = useRef(null), ver = useRef(null); // ver — поточна версія чернетки (відкладене збереження не бере застарілу)
  useEffect(() => { if (data) ver.current = data.draft.version; }, [data]);
  const load = async () => { const x = await api(base); setData(x); setEdits(Object.fromEntries(Object.entries(x.draft.doc.edits || {}).map(([k, v]) => [k, String(v)]))); };
  useEffect(() => { load().catch((e) => setErr(e.message)); }, [draftId]);
  // Збереження: графа 5 і параметри; після кожної зміни — перерахунок на сервері
  const save = async (patch) => {
    setBusy("save"); setErr("");
    try { const x = await api(base, { method: "PUT", body: { version: ver.current, doc: patch } }); setData((d) => ({ ...d, ...x })); }
    catch (e) { setErr(e.message); if (e.status === 409) await load(); }
    finally { setBusy(null); }
  };
  const setEdit = (k, v) => {
    const next = { ...edits, [k]: v };
    setEdits(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const out = Object.fromEntries(Object.entries(next).filter(([, x]) => /^-?\d{1,13}$/.test(String(x).trim().replace(/\s/g, ""))).map(([kk, x]) => [kk, Number(String(x).replace(/\s/g, ""))]));
      save({ edits: out });
    }, 700);
  };
  const reset = (k) => { const next = { ...edits }; delete next[k]; setEdits(next); const out = Object.fromEntries(Object.entries(next).map(([kk, x]) => [kk, Number(x)])); save({ edits: out }); };

  if (err && !data) return <p className="error">{err}</p>;
  if (!data) return <p className="muted" role="status">Завантаження…</p>;
  const { draft, result, locked } = data, doc = draft.doc;
  const reviewed = draft.reviewed?.sha256 && draft.reviewed.sha256 === data.sha256 && draft.reviewed.version === draft.version;
  const review = async () => { setBusy("review"); setErr(""); try { await api(`${base}/review`, { method: "POST", body: { sha256: data.sha256, version: draft.version } }); await load(); } catch (e) { setErr(e.message); } finally { setBusy(null); } };
  const blank = result.ok && data.sha256 ? urBlankHtml(buildUrXml({ org: doc.org, ur: result, period: doc.period, corrects: doc.corrects, hvpp: doc.hvpp }), { cssHref: new URL(formCssUrl, location.href).href }) : null;
  const shown = result.lines.filter((l) => l.section || all || l.keys.some((k) => k.before || k.after || edits[k.key] != null));
  const r18 = result.rows.R180GB, r21 = result.rows.R210GB;

  return (
    <div className="vat-ur">
      <p><a href={`#/c/${companyId}/reports`}>← Звітність</a></p>
      <div className="section-head"><div>
        <h2>Уточнюючий розрахунок з ПДВ за {monthName(doc.corrects)}</h2>
        <p className="status">Графа 4 — з {/^J0217/.test(doc.source.cdoc) ? "останнього уточнюючого розрахунку" : "поданої декларації"} ({doc.source.cdoc}, прийнято {day(doc.source.dget)}).
          Змініть у графі 5 рядки з помилкою — різницю, підсумки й штраф порахуємо самі. Звітний період розрахунку — {monthName(doc.period)}.</p>
      </div></div>

      <div className="card">
        <div className="row toolbar">
          <label className="check"><input type="checkbox" checked={Boolean(doc.hvpp)} disabled={!canEdit || locked} onChange={(e) => save({ hvpp: e.target.checked })} /> Показники цього періоду вже уточнювалися раніше</label>
          <label className="small">Штраф (рядок 18.1){" "}
            <select value={doc.finePct ?? UR_FINE_PCT} disabled={!canEdit || locked} onChange={(e) => save({ finePct: Number(e.target.value) })}>
              <option value={UR_FINE_PCT}>{UR_FINE_PCT} % — п. 50.1 ПКУ</option><option value={0}>0 % — пільга (перевірте підставу)</option>
            </select></label>
          {(result.rows.R190GB.after > 0 || result.rows.R190GB.before > 0) && (
            <label className="small">Сума за п. 200¹.3 на момент подання{" "}
              <input className="input" inputMode="numeric" style={{ width: 130 }} defaultValue={doc.regSum ?? ""} disabled={!canEdit || locked}
                onBlur={(e) => { const v = e.target.value.trim().replace(/\s/g, ""); save({ regSum: v === "" ? null : Number(v) }); }} /> грн</label>)}
          <label className="check small"><input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Показати всі рядки</label>
          {busy === "save" && <span className="muted small">зберігаємо…</span>}
        </div>
        <div className="table-wrap" tabIndex={0} role="region" aria-label="Рядки уточнюючого розрахунку">
          <table className="table-static vat-ur-table">
            <thead><tr><th>Рядок</th><th>Назва</th><th>Кол.</th><th className="num">4 — було</th><th className="num">5 — правильно</th><th className="num">6 — різниця</th></tr></thead>
            <tbody>{shown.map((l) => l.section
              ? <tr key={l.line} className="sec"><td colSpan={6}><b>{l.label}</b></td></tr>
              : l.keys.map((k, i) => (
                <tr key={k.key} className={`${l.total ? "tot" : ""}${k.diff ? " changed" : ""}`}>
                  <td>{i ? "" : l.line}</td><td className="wrap-cell">{i ? "" : l.label}</td><td>{k.key.endsWith("A") ? "А" : "Б"}</td>
                  <td className="num">{grn(k.before)}</td>
                  <td className="num">{k.input && canEdit && !locked
                    ? <span className="vat-ur-cell"><input className="input" inputMode="numeric" value={edits[k.key] ?? String(k.after)} onChange={(e) => setEdit(k.key, e.target.value)}
                        aria-label={`Рядок ${l.line}, колонка ${k.key.endsWith("A") ? "А" : "Б"}, уточнений показник`} />
                        {edits[k.key] != null && <button type="button" className="linkish small" onClick={() => reset(k.key)} title="Повернути показник декларації">↺</button>}</span>
                    : grn(k.after)}</td>
                  <td className={`num${k.diff > 0 ? " up" : k.diff < 0 ? " down" : ""}`}>{k.diff ? `${k.diff > 0 ? "+" : ""}${grn(k.diff)}` : ""}</td>
                </tr>)))}</tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <h3>Підсумок</h3>
        <ul className="small">
          <li>До сплати (рядок 18): було {money(r18.before)}, стало {money(r18.after)} — різниця <b>{r18.diff > 0 ? "+" : ""}{money(r18.diff)} грн</b>.</li>
          {result.fine > 0 && <li>Штраф (рядок 18.1): <b>{money(result.fine)} грн</b>.</li>}
          {r21.diff !== 0 && <li>Від'ємне значення до наступного періоду (рядок 21): було {money(r21.before)}, стало {money(r21.after)}.</li>}
          {result.pay > 0 && <li><b>Сплатіть до подання: {money(result.pay)} грн</b> — на рахунок для сплати ПДВ.</li>}
        </ul>
        {result.warnings.map((w, i) => <p key={i} className="notice small">{w}</p>)}
        {result.errors.map((e, i) => <p key={i} className="error small" role="alert">{e}</p>)}
        {err && <p className="error small" role="alert">{err}</p>}
      </div>

      {blank && (
        <div className="card">
          <h3>Бланк</h3>
          <ScaledForm html={blank} width={A4} title="Уточнюючий розрахунок — бланк" />
          {reviewed ? <p className="pn-ok">✓ Переглянуто {day(draft.reviewed.at)}. Будь-яка зміна скине підтвердження.</p>
            : canEdit && !locked && <button type="button" className="btn" disabled={Boolean(busy)} onClick={review}>Я переглянув(-ла) уточнюючий розрахунок</button>}
        </div>
      )}
      {(reviewed || locked) && <UrSign companyId={companyId} company={company} draftId={draftId} version={version} />}
    </div>
  );
}

// Підпис і подання — бланк з байтів, які дав кабінет ДПС
function UrSign({ companyId, company, draftId, version }) {
  const blanks = (sub) => {
    const xml = decodeCp1251(b64ToBytes(sub.docs[0].dpsXml)), t = (k) => (xml.match(new RegExp(`<${k}>([^<]*)</${k}>`)) || [])[1] || "";
    return { tabs: [{ key: "ur", label: "Уточнюючий розрахунок", html: urBlankHtml(xml, { cssHref: new URL(formCssUrl, location.href).href }) }], signer: { kbos: t("HKBOS"), bos: t("HBOS") } };
  };
  return <ReportSign companyId={companyId} company={company} draftId={draftId} apiKey="vat-ur" blanks={blanks} what="уточнюючий розрахунок" version={version} />;
}
